import smoothscroll from "smoothscroll-polyfill";
import { useEffect, useRef, useCallback } from "react";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useEffectOnce } from "react-use";
import { Route, Routes, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/common/errorBoundary";
import { GlobalStyles } from "./components/common/globalStyles";
import { Layout } from "./components/common/layout";
import { UIContextProvider } from "./contexts/uiContext";

import { useAuth } from "./hooks/useAuth";
import { useAuthHandle } from "./hooks/useAuthHandle";
import {
  GatewayContainer,
  GenericErrorContainer,
  HomeContainer,
  LoginContainer,
  NovelContainer,
  NovelEpisodeReorderContainer,
  EpisodeWriteContainer,
  EpisodePreviewContainer,
} from "./routes";

import { AuthUseCase } from "./usecases/authUseCase";

import "./assets/transitions.css";
import { ContestListContainer } from "./pageContainers/contestListContainer";
import { ContestEntryContainer } from "./pageContainers/contestEntryContainer";
import { ToastProvider } from "./providers/toastProvider";
import { LogoutContainer } from "./pageContainers/logoutContainer";

export const SESSION_KEY = "session";

const authUseCase = new AuthUseCase();

const AnimatedSwitch = (): JSX.Element => {
  const location = useLocation();
  const prev = useRef<string>();

  useEffect(() => {
    prev.current = location.pathname;
  }, [location]);

  const getPathDepth = useCallback((pathname?: string): number => {
    if (!pathname) {
      return 0;
    }
    let pathArr = pathname.split("/");
    pathArr = pathArr.filter((n) => n !== "");
    return pathArr.length;
  }, []);

  const noAnimation = location.search.indexOf("notransition") > -1;
  const toWritePage = location.pathname.indexOf("/episodes/") > -1;
  const fromWritePage = (prev.current || "").indexOf("/episodes/") > -1;
  const enterDirection = fromWritePage || toWritePage ? "up" : "left";
  const exitDirection = fromWritePage || toWritePage ? "down" : "right";
  const direction =
    getPathDepth(location.pathname) - getPathDepth(prev.current) >= 0
      ? enterDirection
      : exitDirection;

  const animationClass = noAnimation ? "nothing" : direction;

  return (
    <ToastProvider>
      <TransitionGroup>
        <CSSTransition
          key={location.key}
          classNames="pageSlider"
          timeout={{ enter: 200, exit: 100 }}
        >
          <div className={animationClass}>
            <Routes location={location}>
              <Route path="/gateway" element={<GatewayContainer />} />
              <Route path="/login" element={<LoginContainer />} />
              <Route path="/" element={<HomeContainer />} />
              <Route path="/contests" element={<ContestListContainer />} />
              <Route
                path="/contests/:contestId/entry"
                element={<ContestEntryContainer />}
              />
              <Route path="/novel/:novelId" element={<NovelContainer />} />
              <Route
                path="/novel/:novelId/reorder"
                element={<NovelEpisodeReorderContainer />}
              />
              <Route
                path="/novel/:novelId/episodes/:episodeId"
                element={<EpisodeWriteContainer />}
              />
              <Route
                path="/preview/:novelId/episodes/:episodeId"
                element={<EpisodePreviewContainer />}
              />
              <Route path="/error/:type" element={<GenericErrorContainer />} />
              <Route path="/logout" element={<LogoutContainer />} />
              <Route element={<GenericErrorContainer />} />
            </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </ToastProvider>
  );
};

export const App: React.FC = () => {
  useAuthHandle();
  const { user } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const webToken = urlParams.get("webToken");
  const location = useLocation();
  // Add support for smooth scrolling in iOS webview
  useEffectOnce(() => {
    smoothscroll.polyfill();
  });

  useEffect(() => {
    const redirectToWebLogin = (): void => {
      if (window.location.hostname === "localhost") {
        return;
      }
      const fromParam = `&from=${encodeURIComponent(
        `/novel-studio${location.pathname}`,
      )}`;
      window.location.href = `//${process.env.REACT_APP_WEB_DOMAIN}/auth/login?nolayout=1${fromParam}`;
    };

    const loginByWebToken = async (stToken: string): Promise<void> => {
      await authUseCase.loginByToken(stToken).catch(() => {
        redirectToWebLogin();
      });
    };

    if (
      user !== undefined &&
      user === null &&
      !token &&
      location.pathname !== "/error" &&
      location.pathname !== "/logout"
    ) {
      if (user === null) {
        if (webToken) {
          // Check if we are coming from Studio and sign in with the same user
          loginByWebToken(webToken);
        } else {
          setTimeout(() => {
            if (user === null) {
              redirectToWebLogin();
            }
          }, 1000);
        }
      }
    } else if (user && user.isAnonymous) {
      setTimeout(() => {
        redirectToWebLogin();
      }, 1000);
    }
  }, [location.pathname, token, user, webToken]);

  return (
    <>
      <GlobalStyles />
      <UIContextProvider>
        <Layout>
          <ErrorBoundary>
            <AnimatedSwitch />
          </ErrorBoundary>
        </Layout>
      </UIContextProvider>
    </>
  );
};
